import { Alert, Spin } from "antd";
import { useTranslation } from "react-i18next";
import {
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SeriesDefinition, getAdaptiveTickFormatter } from "./Chart";

interface DualChartProps {
  seriesDefintions: SeriesDefinition[];
  isLoading: boolean;
  height?: string;
}

export function DualAxisChart({ seriesDefintions, isLoading, height }: DualChartProps) {
  const { t } = useTranslation();

  if (isLoading) {
    return <Spin className="centered" />;
  }
  if (
    seriesDefintions[0].dataSet.length === 0 &&
    seriesDefintions[1].dataSet.length === 0
  ) {
    return (
      <div style={{ height: "100%", position: "relative" }}>
        <Alert message={t("no-data")} type="info" showIcon />
      </div>
    );
  }

  const mergedData = seriesDefintions[0].dataSet.map((item, index) => ({
    ts: new Date(item.ts).getTime(),
    value: item.value,
    value2: seriesDefintions[1].dataSet[index]?.value ?? null,
  }));

  return (
    <div
      data-testid="dual-axis-chart"
      style={{ width: "100%", height: "100%" }}
    >
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={mergedData}
          margin={{ top: 20, right: 50, left: 20, bottom: 20 }}
        >
          <XAxis
            dataKey="ts"
            type="number"
            domain={["auto", "auto"]}
            scale="time"
            tickFormatter={getAdaptiveTickFormatter(mergedData)}
            tickCount={5}
            interval="preserveStartEnd"
          />
          <YAxis domain={["auto", "auto"]} yAxisId="left" />
          <YAxis
            domain={["auto", "auto"]}
            yAxisId="right"
            orientation="right"
          />
          <Tooltip
            labelFormatter={(ts) => new Date(ts).toLocaleString()}
            formatter={(value: number) => value.toFixed(2)}
          />
          <Legend iconType="plainline" />
          <Line
            yAxisId="left"
            type="linear"
            dataKey="value"
            stroke={seriesDefintions[0].color}
            name={seriesDefintions[0].name}
            dot={false}
            isAnimationActive={false}
            strokeWidth={2}
          />
          <Line
            yAxisId="right"
            type="linear"
            dataKey="value2"
            stroke={seriesDefintions[1].color}
            name={seriesDefintions[1].name}
            dot={false}
            isAnimationActive={false}
            strokeWidth={2}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
